import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userRequest } from '../utils/requestMethods';
import DataTable from 'react-data-table-component';
import { FiArrowRight, FiDownload, FiCreditCard, FiClock, FiSend } from 'react-icons/fi';
import { MdSearch } from 'react-icons/md';
import { formatCurrency } from '../utils/currencyUtils';
import { CustomPagination, Header, WithdrawModal } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import useKYCStatusRedirect from '../hooks/useKYCStatusRedirect';
import { exportToCSV } from '../utils/exportCSV';
import SkeletonTable from '../components/common/SkeletonTable';

const customStyles = {
  headRow: {
    style: { backgroundColor: 'rgb(248, 250, 252)', fontWeight: 'bold' },
  },
};

const statusMeta = {
  successful: { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Successful' },
  failed:     { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Failed'     },
  pending:    { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending'    },
};

const Withdraw = () => {
  useKYCStatusRedirect("/withdraw");
  const { user, school } = useStateContext();
  const userId = user?._id;
  const schoolID = school?._id;
  const [balance, setBalance] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [bankDetails, setBankDetails] = useState({});
  const [amount, setAmount] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFundId, setSelectedFundId] = useState('');
  const [filterText, setFilterText] = useState('');

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  useEffect(() => {
    if (!schoolID) return;
    const fetchBalance = async () => {
      try {
        const { data } = await userRequest.get(`banking/wallets/${schoolID}/balance`);
        setBalance(data.data ?? data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchBalance();
  }, [schoolID, refresh]);

  useEffect(() => {
    if (!userId || !schoolID) return;
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const { data } = await userRequest.get(`transactions/${userId}/${schoolID}?type=withdrawal&sort_by=date&order=des`);
        setTransactions(data.transactions || []);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [userId, schoolID, refresh]);

  useEffect(() => {
    if (school) {
      setBankDetails({
        account_name: school.counter_party?.account_name,
        account_number: school.counter_party?.account_number,
        bank_name: school.counter_party?.bank_name,
        bank_code: school.counter_party?.bank_code,
      });
    }
  }, [school]);

  const resetForm = () => {
    setAmount('');
    setOtp('');
    setError('');
    setSuccess(false);
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!amount || !otp) {
      setError('Please fill all the fields');
      return;
    }
    setSubmitting(true);
    try {
      await userRequest.post('banking/withdrawals', {
        schoolId: schoolID,
        fundId: selectedFundId || undefined,
        fund_id: selectedFundId || undefined,
        amount: Number(amount),
        destinationBankCode: bankDetails.bank_code,
        destinationAccountNumber: bankDetails.account_number,
        destinationAccountName: bankDetails.account_name,
        narration: 'School Withdrawal',
        otp,
      });
      setSuccess(true);
      setRefresh(r => !r);
      setTimeout(() => { resetForm(); toggleModal(); }, 2000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Withdrawal failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      name: 'Amount',
      cell: row => <span className="font-semibold text-gray-800">{formatCurrency(row.amount)}</span>,
      exportCell: row => formatCurrency(row.amount),
    },
    {
      name: 'Transaction Fee',
      cell: row => <span className="text-gray-500">{formatCurrency(row.transaction_fee || 0)}</span>,
      exportCell: row => formatCurrency(row.transaction_fee || 0),
    },
    {
      name: 'Final Amount',
      cell: row => <span className="font-medium text-red-600">{formatCurrency(row.final_amount)}</span>,
      exportCell: row => formatCurrency(row.final_amount),
    },
    {
      name: 'Status',
      cell: row => {
        const s = statusMeta[row?.status] || statusMeta.pending;
        return (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${s.bg} ${s.text}`}>
            {s.label}
          </span>
        );
      },
      exportCell: row => row.status || 'N/A',
    },
    {
      name: 'Date',
      cell: row => new Date(row.created_at).toLocaleDateString('en-NG', { dateStyle: 'medium' }),
      sortable: true,
    },
    {
      cell: row => (
        <Link to={`/expense-details/${row._id}`}>
          <button className="p-2 rounded-full text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors">
            <FiArrowRight className="w-4 h-4" />
          </button>
        </Link>
      ),
      ignoreRowClick: true,
      button: true,
      width: '56px',
    },
  ];

  const filteredData = transactions?.filter(item =>
    item.metadata?.reason?.toLowerCase().includes(filterText.toLowerCase()) ||
    item.amount?.toString().includes(filterText)
  ) || [];

  return (
    <div className="m-4 md:m-10 mt-24 p-6 md:p-10 bg-white dark:bg-secondary-dark-bg rounded-3xl min-h-screen">

      {/* Page header */}
      <div className="flex items-start justify-between mb-6">
        <Header
          category="Finance"
          title="Withdraw Balance"
          description="Withdraw available fund balances directly to your verified official school bank account."
        />
        <button
          type="button"
          onClick={toggleModal}
          className="inline-flex items-center gap-2 py-2.5 px-5 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <FiSend className="w-4 h-4" />
          Withdraw
        </button>
      </div>

      {/* Balance + bank cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="rounded-2xl border border-blue-100 bg-blue-50 px-5 py-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white rounded-xl">
              <FiCreditCard className="text-blue-600 text-lg" />
            </div>
            <p className="text-xs font-medium text-gray-500">Available Balance</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(balance?.availableBalance)}</p>
        </div>

        <div className="rounded-2xl border border-yellow-100 bg-yellow-50 px-5 py-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white rounded-xl">
              <FiClock className="text-yellow-600 text-lg" />
            </div>
            <p className="text-xs font-medium text-gray-500">Pending Balance</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(balance?.pendingBalance)}</p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-gray-50 px-5 py-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white rounded-xl">
              <FiSend className="text-gray-500 text-lg" />
            </div>
            <p className="text-xs font-medium text-gray-500">Withdrawal Account</p>
          </div>
          {bankDetails?.account_name ? (
            <>
              <p className="text-sm font-semibold text-gray-800">{bankDetails.account_name}</p>
              <p className="text-sm text-gray-600 mt-0.5">{bankDetails.account_number}</p>
              <p className="text-xs text-gray-400 mt-0.5">{bankDetails.bank_name}</p>
            </>
          ) : (
            <p className="text-sm text-gray-400">No account configured</p>
          )}
        </div>
      </div>

      {/* Withdrawal history */}
      <div className="rounded-2xl border border-gray-200 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="text-sm font-semibold text-gray-700">Withdrawal History</p>
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <MdSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                placeholder="Search..."
                className="pl-9 pr-4 py-2 text-sm rounded-full bg-gray-50 border border-gray-200 focus:ring-indigo-500 focus:border-indigo-500 w-44"
              />
            </div>
            <button
              onClick={() => exportToCSV(filteredData, columns, 'withdrawals.csv')}
              className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        <DataTable
          data={filteredData}
          columns={columns}
          getRowId={row => row._id}
          pagination
          paginationComponent={CustomPagination}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={<SkeletonTable rows={4} columns={5} />}
          highlightOnHover
          persistTableHead
          noDataComponent={
            <div className="py-12 text-center text-sm text-gray-400">No withdrawal history found.</div>
          }
        />
      </div>

      {isModalOpen && (
        <WithdrawModal
          closeModal={toggleModal}
          amount={amount}
          setAmount={setAmount}
          otp={otp}
          setOtp={setOtp}
          bankDetails={bankDetails}
          balance={balance}
          error={error}
          success={success}
          loading={submitting}
          submitHandler={submitHandler}
          school={school}
          onFundSelect={setSelectedFundId}
        />
      )}
    </div>
  );
};

export default Withdraw;
